<script setup lang="ts">
// Use Nuxt auto-imports and Lazy prefix for better performance
const { t, locale } = useI18n()
const { canonicalFor, absUrl } = useSiteUrl()
const localize = useLocalizedPath()

definePageMeta({
  layout: false,
})

// SEO Meta
useSeoMeta({
  title: () => t('seo.homepage.title'),
  description: () => t('seo.homepage.description'),
  ogTitle: () => t('seo.homepage.title'),
  ogDescription: () => t('seo.homepage.description'),
  ogImage: () => absUrl('/og/home.png?v=2'),
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: () => canonicalFor(localize('/')),
  ogSiteName: 'InMyResume',
  twitterCard: 'summary_large_image',
})

// JSON-LD: one linked @graph rather than three separate scripts each repeating
// its own inline Organization.
const {
  organizationNode,
  webSiteNode,
  softwareApplicationNode,
  webPageNode,
  useJsonLdGraph,
} = useJsonLd()

useJsonLdGraph(() => [
  organizationNode(),
  webSiteNode(),
  softwareApplicationNode(),
  webPageNode({ path: '/', name: t('seo.homepage.title'), description: t('seo.homepage.description') }),
])

// NOTE: no <link rel=preload> for /Adam.png or /head.png. Neither URL is ever
// requested by this page: the hero uses assets/head.png through CSS (hashed URL)
// and Adam.png is served via <NuxtImg> as /_vercel/image?url=%2FAdam.png&w=…,
// so the preloads only downloaded ~3 MB at high priority and threw it away.

const { isAuthenticated } = useAuthUser()

// The hero opens on the chat CV builder; its funnel events feed /admin/ab-test.
const { resolve: resolveHero, track: trackHero } = useHeroExperiment()

onMounted(resolveHero)
</script>

<template>
  <NuxtLayout name="sell" :show-back-button="false">
    <!-- Each marketing section opts into the `sofax` design system itself, so
         the blog list and the generator keep their own typography. -->
    <!-- Below the hero every section is server-rendered as usual but hydrated
         only when it scrolls into view (Nuxt lazy hydration), so their
         JavaScript is not part of the first load. A mobile Lighthouse run had
         measured ~10 s of main-thread work on this page. -->
    <div class="w-full bg-white dark:bg-gray-950">
      <!-- Hero: the visitor picks how to build the CV — chat with the AI
           (default), paste or write it all at once, or the classic form -->
      <LandingCvCreateModes :on-event="trackHero" />

      <!-- Live counters where the template shows client logos -->
      <SofaxTrustStrip />

      <!-- 2.5-minute walkthrough of the whole app; plays only on demand -->
      <LazyVisibleSofaxVideoWalkthrough hydrate-on-visible />

      <!-- Example of what comes out: a PDF plus the web CV it publishes -->
      <LazyVisibleSofaxOutputShowcase hydrate-on-visible />

      <!-- Every feature has its own page; this grid links them -->
      <LazyVisibleSofaxFeatureGrid hydrate-on-visible />

      <!-- The Civil Service and NHS writers: English only, like their pages -->
      <LazyVisibleSofaxUkApplicationsSection v-if="locale === 'en'" hydrate-on-visible />

      <!-- Tailoring a CV to one job posting, with a link to its page -->
      <LazyVisibleSofaxTailorSection hydrate-on-visible />

      <!-- The rest of the editor: example phrases, translation, cover letter PDF, autosave -->
      <LazyVisibleSofaxEditorExtrasSection hydrate-on-visible />

      <!-- The Applications board, with a link to its page -->
      <LazyVisibleSofaxTrackerSection hydrate-on-visible />

      <!-- Two alternating showcases: the builder itself and the web CV -->
      <LazyVisibleSofaxContentShowcase hydrate-on-visible />

      <!-- Dark block: the channels one CV can go out through -->
      <LazyVisibleSofaxIntegrationSection hydrate-on-visible />

      <!-- Price ladder; full comparison lives on /pricing -->
      <LazyVisibleSofaxPricingTeaser hydrate-on-visible />

      <!-- Template's testimonial block, on the grey surface between pricing and FAQ -->
      <div class="surface-alt">
        <LazyVisibleSofaxTestimonials hydrate-on-visible />
      </div>

      <LazyVisibleSofaxFaqSection hydrate-on-visible />

      <!-- Teaser for the interview guide, next to the other content (the blog) -->
      <LazyVisibleSofaxInterviewPrepSection hydrate-on-visible />

      <div id="blog" class="bg-surface dark:bg-gray-900">
        <LazyHomepageBlogSection />
      </div>

      <LazyVisibleSofaxCtaSection hydrate-on-visible />
    </div>

    <ClientOnly>
      <LazyCommonNewsletterDialog v-if="!isAuthenticated" source="download" />
    </ClientOnly>
  </NuxtLayout>
</template>
